import React from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import date from 'date-and-time';
import { TypedMemo } from '../../../common/types';
import { ArtistStatsCard } from '../ArtistStatsCard/ArtistStatsCard';
import { useArtistPlaylistAdditions } from '../../hooks/useArtistPlaylistAdditions';
import { useArtistPlaylistAppearances } from '../../hooks/useArtistPlaylistAppearances';
import { useEntityIdParam } from '../../hooks/useEntityIdParam';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { useArtistListeningScore } from '../../hooks/useArtistListeningScore';
import { useCanViewListeningInsights } from '../../hooks/useCanViewListeningInsights';
import { useArtistReach } from '../../hooks/useArtistReach';
import { ReachCard } from '../ReachCard/ReachCard';

interface ArtistStatsRowProps {
  // No props needed - component will use hooks directly
}

const ArtistStatsRowComponent: React.FC<ArtistStatsRowProps> = () => {
  const intl = useIntl();
  const artistId = useEntityIdParam('artist');
  const { selectedDate, rangeDays } = useSMLDatePickerDateParam();

  const {
    data: playlistAdditionsData,
    isLoading: isPlaylistAdditionsLoading,
    error: playlistAdditionsError,
  } = useArtistPlaylistAdditions({
    artistId: artistId || '',
    date: selectedDate,
  });

  const {
    data: playlistAppearancesData,
    isLoading: isPlaylistAppearancesLoading,
    error: playlistAppearancesError,
  } = useArtistPlaylistAppearances({
    artistId: artistId || '',
    date: selectedDate,
  });

  const canViewListeningInsights = useCanViewListeningInsights();

  const {
    data: listeningData,
    isLoading: isListeningLoading,
    error: listeningError,
  } = useArtistListeningScore({ artistId: artistId || '', date: selectedDate, enabled: canViewListeningInsights });

  const {
    data: reachData,
    isLoading: isReachLoading,
    error: reachError,
    refetch: refetchReach,
  } = useArtistReach({ artistId: artistId || '', date: selectedDate, rangeDays });

  // Format the selected date for display
  const formattedDate = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <Row>
      {/* Added To Playlists Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.artist.details.added-to-playlists-score',
            defaultMessage: 'Playlist Gain Score',
          })}
          data={playlistAdditionsData?.addedToPlaylists}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="score"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.artist.details.added-to-playlists-score.tooltip',
            defaultMessage:
              "Shows how many times this artist's tracks were added to playlists per 10,000 tracked playlists. Higher scores indicate growing popularity.",
          })}
        />
      </Col>

      {/* Removed From Playlists Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.artist.details.removed-from-playlists-score',
            defaultMessage: 'Playlist Loss Score',
          })}
          data={playlistAdditionsData?.removedFromPlaylists}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="score"
          date={formattedDate}
          invertColorLogic
          tooltipText={intl.formatMessage({
            id: 'app.sml.artist.details.removed-from-playlists-score.tooltip',
            defaultMessage:
              "Shows how many times this artist's tracks were removed from playlists per 10,000 tracked playlists. Lower scores are better.",
          })}
        />
      </Col>

      {/* Playlist Visibility Score */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.artist.details.playlist-visibility-score',
            defaultMessage: 'Playlist Visibility Score',
          })}
          data={playlistAppearancesData?.appearedInPlaylists}
          isLoading={isPlaylistAppearancesLoading}
          error={playlistAppearancesError}
          unit="score"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.artist.details.playlist-visibility-score.tooltip',
            defaultMessage:
              "Shows how many playlists this artist's tracks appear in per 10,000 tracked playlists. Indicates overall playlist presence.",
          })}
        />
      </Col>

      {/* Chart playlist placements (raw count, not a score) */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.artist.details.chartPlaylistAdds',
            defaultMessage: 'Chart playlist adds',
          })}
          data={playlistAdditionsData?.chartPlaylistAdds}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="adds"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.artist.details.chartPlaylistAdds.tooltip',
            defaultMessage: "How many times this artist's tracks were added to chart/editorial playlists on this day.",
          })}
        />
      </Col>

      {/* Reach score (audience-weighted adds) */}
      <Col xs={12} md={4} className="mb-3">
        <ArtistStatsCard
          title={intl.formatMessage({
            id: 'app.sml.artist.details.reachScore',
            defaultMessage: 'Reach score',
          })}
          data={playlistAdditionsData?.reachScore}
          isLoading={isPlaylistAdditionsLoading}
          error={playlistAdditionsError}
          unit="reach"
          date={formattedDate}
          tooltipText={intl.formatMessage({
            id: 'app.sml.artist.details.reachScore.tooltip',
            defaultMessage:
              'Adds weighted by playlist audience (1 + followers/10,000 per tracked-playlist add) — not a raw follower count.',
          })}
        />
      </Col>

      {/* Audience reach (real people, not a score) */}
      <Col xs={12} md={8} className="mb-3">
        <ReachCard
          entityType="artist"
          summary={reachData?.summary}
          series={reachData?.series ?? []}
          isLoading={isReachLoading}
          error={reachError}
          retry={() => {
            refetchReach().catch(console.error);
          }}
        />
      </Col>

      {canViewListeningInsights && (
        <Col xs={12} md={4} className="mb-3">
          <ArtistStatsCard
            title={intl.formatMessage({
              id: 'app.sml.artist.details.listening-score-card',
              defaultMessage: 'Listening Score',
            })}
            data={listeningData?.listeningScore}
            isLoading={isListeningLoading}
            error={listeningError}
            unit="score"
            date={formattedDate}
            tooltipText={intl.formatMessage({
              id: 'app.sml.artist.details.listening-score-card.tooltip',
              defaultMessage:
                "How much an anonymized sample of users is actually listening to this artist's tracks, relative to all listening activity.",
            })}
          />
        </Col>
      )}
    </Row>
  );
};

export const ArtistStatsRow = TypedMemo(ArtistStatsRowComponent);
